import React, { useCallback, useEffect, useState } from 'react';
import { Col, Select, Typography, Tabs, Tag } from 'antd';
import { PageHeader } from '../../components/layout/PageHeader';
import { ChartScorePerField } from './charts/ChartScorePerField';
import { ChartScorePerfect } from './charts/ChartScorePerfect';
import { ChartVersionProgress } from './charts/ChartVersionProgress';
import { ChartIncorrectPerField } from './charts/ChartIncorrectPerField';
import { DatasetStatistic, Dataset } from '../../types/dataset';
import { AlgorithmVersion, Algorithm } from '../../types';
import { AlgorithmStatistic } from '../../types/result';
import { getScoringOverview } from '../../lib/api/aws-api';
import {
  getDatasetScoreByAlgorithmVersion,
  getDataset,
  getSecondToLatestDatasetVersion,
} from '../../lib/api/dataset-api';
import { getAlgorithm } from '../../lib/api/algorithm-api';
import { deductLatestAlgorithmVersions, getAlgorithmValue } from '../../lib/util/algorithm';
import { useParams } from 'react-router-dom';
import { Spinner } from '../../components/loading/Spinner';

const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;

const AlgorithmDetailOverview = () => {
  const { algorithmId } = useParams() as { algorithmId: string };

  const [algorithm, setAlgorithm] = useState<null | Algorithm>(null);
  const [algorithmVersions, setAlgorithmVersions] = useState<AlgorithmVersion[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [statistics, setStatistics] = useState<DatasetStatistic[]>([]);

  const [selectedDataset, setSelectedDataset] = useState<null | Dataset>(null);
  const [selectedDatasetStatistics, setSelectedDatasetStatistics] = useState<null | DatasetStatistic>();

  const [compareVersionId, setCompareVersionId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('1');

  const fetchData = useCallback(
    async algorithmId => {
      const algorithm = await getAlgorithm(algorithmId);
      setAlgorithm(algorithm || null);
    },
    [setAlgorithm]
  );

  useEffect(() => {
    if (algorithmId) {
      fetchData(algorithmId);
    }
  }, [algorithmId, fetchData]);

  useEffect(() => {
    if (algorithm) {
      const getVersions = async () => {
        const showableVersions = await deductLatestAlgorithmVersions(algorithm.id);
        setAlgorithmVersions(showableVersions);
      };
      getVersions();
    }
  }, [algorithm]);

  const fetchStatistics = async () => {
    if (algorithm) {
      const scoringsPerClient = await getScoringOverview();
      const scoringsForThisAlgorithm = scoringsPerClient.filter(scoring => {
        return scoring.algorithm_id === algorithm.id;
      });
      const datasets = await Promise.all(
        scoringsForThisAlgorithm.map(async scoring => {
          const dataset = await getDataset(scoring.dataset_id);
          setDatasets(prev => [...prev, dataset]);
          return dataset;
        })
      );
      if (datasets.length > 0 && algorithmVersions.length) {
        const allStats: DatasetStatistic[] = [];
        await Promise.all(
          datasets.map(async (dataset: Dataset, index: number) => {
            const stats: AlgorithmStatistic[] = [];
            const datasetVersion = await getSecondToLatestDatasetVersion(dataset.id);
            await Promise.all(
              algorithmVersions
                .sort((a, b) => (a.timeCreated < b.timeCreated ? 1 : -1))
                .map(async (algorithmVersion: AlgorithmVersion, index: number) => {
                  const request = await getDatasetScoreByAlgorithmVersion(
                    datasetVersion.version_id,
                    algorithmVersion.id
                  );
                  if (request)
                    stats.push({
                      summary: request.summary,
                      algorithmVersion: algorithmVersion,
                    });
                })
            );
            const sortedStats = stats.sort((a, b) =>
              a.algorithmVersion.timeCreated < b.algorithmVersion.timeCreated ? 1 : -1
            );
            allStats.push({
              dataset: dataset,
              stats: sortedStats,
            });
          })
        );

        setStatistics(allStats);
      }
    }
  };

  useEffect(() => {
    if (algorithm && algorithmVersions.length) {
      fetchStatistics();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithm, algorithmVersions]);

  useEffect(() => {
    if (selectedDataset) {
      const selection = statistics.find(statistic => statistic.dataset.id === selectedDataset.id)!;
      setSelectedDatasetStatistics(selection);
    } else {
      setSelectedDatasetStatistics(null);
    }
  }, [selectedDataset, statistics]);

  return (
    <>
      <PageHeader
        back={{ url: '/overview', label: 'Overview' }}
        header={'Overview: ' + (algorithm && getAlgorithmValue(algorithm.name)) || null}
        description={`This is an overview of the '${(algorithm && getAlgorithmValue(algorithm.name)) ||
          null}' algorithm statistics.`}
      />
      <Col className="mt-8 flex align-center">
        <div style={{ marginRight: 100 }}>
          <Text style={{ marginRight: 14 }}>Select a dataset: </Text>
          <Select
            value={selectedDataset?.id || 'avg'}
            style={{ width: '150px' }}
            onChange={(value: any) => {
              if (value === 'avg') {
                setSelectedDataset(null);
              }
              const dataset = datasets.find(l => l.id === value)!;
              setSelectedDataset(dataset);
            }}>
            <Option key="avg" value="avg">
              Average
            </Option>
            {datasets.map(dataset => (
              <Option key={dataset.id} value={dataset.id}>
                {dataset.name}
              </Option>
            ))}
          </Select>
        </div>
        <div>
          {activeTab === '1' && <Text></Text>}
          {activeTab !== '1' && selectedDataset && selectedDatasetStatistics?.stats.length && (
            <>
              <Tag>{selectedDatasetStatistics.stats[0].algorithmVersion.version}</Tag>
              {selectedDatasetStatistics.stats.length > 1 && (
                <>
                  <Text className="mr-4">Compare with:</Text>
                  <Select
                    disabled={activeTab === '1'}
                    style={{ width: '90px' }}
                    value={compareVersionId || 0}
                    defaultValue={0}
                    onChange={(value: any) => {
                      setCompareVersionId(value);
                    }}>
                    <Option value={0} key="none">
                      None
                    </Option>
                    {selectedDatasetStatistics &&
                      selectedDatasetStatistics.stats.length &&
                      selectedDatasetStatistics.stats
                        .filter((element, i) => i > 0)
                        .map(stat => (
                          <Option key={stat.algorithmVersion.id} value={stat.algorithmVersion.id}>
                            {stat.algorithmVersion.version}
                          </Option>
                        ))}
                  </Select>
                </>
              )}
            </>
          )}
          {activeTab !== '1' && !selectedDataset && algorithmVersions.length && (
            <>
              <Tag>{algorithmVersions[0].version}</Tag>
              {algorithmVersions.length > 1 && (
                <>
                  <Text className="mr-4">Compare with:</Text>
                  <Select
                    disabled={activeTab === '1'}
                    style={{ width: '90px' }}
                    value={compareVersionId || 0}
                    defaultValue={0}
                    onChange={(value: any) => {
                      setCompareVersionId(value);
                    }}>
                    <Option value={0} key="none">
                      None
                    </Option>
                    {algorithmVersions &&
                      algorithmVersions.length &&
                      algorithmVersions
                        .filter((element, i) => i > 0)
                        .map(version => (
                          <Option key={version.id} value={version.id}>
                            {version.version}
                          </Option>
                        ))}
                  </Select>
                </>
              )}
            </>
          )}
        </div>
      </Col>

      <Col style={{ marginTop: 14 }}>
        {statistics.length && algorithmVersions.length ? (
          <>
            <Tabs activeKey={activeTab} animated={false} onChange={setActiveTab}>
              <TabPane tab="Version Progress" key="1">
                <Col style={{ padding: '1em 0' }}>
                  <ChartVersionProgress
                    statistics={statistics}
                    selectedDataset={selectedDataset}
                    selectedDatasetStatistics={selectedDatasetStatistics || null}
                  />
                </Col>
              </TabPane>
              <TabPane tab="Scores Per Field" key="2">
                <Col style={{ padding: '1em 0 4em 0' }}>
                  <div>
                    <ChartScorePerField
                      statistics={statistics}
                      selectedDataset={selectedDataset}
                      selectedDatasetStatistics={selectedDatasetStatistics || null}
                      compareVersionId={compareVersionId}
                      algorithmVersions={algorithmVersions}
                    />
                  </div>
                  <div style={{ height: 50 }}></div>
                  <div>
                    <ChartIncorrectPerField
                      statistics={statistics}
                      selectedDataset={selectedDataset}
                      selectedDatasetStatistics={selectedDatasetStatistics || null}
                      compareVersionId={compareVersionId}
                      algorithmVersions={algorithmVersions}
                    />
                  </div>
                </Col>
              </TabPane>
              <TabPane tab="Other information" key="3">
                <Col span={10} style={{ padding: '1em 0' }}>
                  <div>
                    <ChartScorePerfect
                      statistics={statistics}
                      selectedDataset={selectedDataset}
                      selectedDatasetStatistics={selectedDatasetStatistics || null}
                      compareVersionId={compareVersionId}
                      algorithmVersions={algorithmVersions}
                    />
                  </div>
                </Col>
              </TabPane>
            </Tabs>
          </>
        ) : (
          <Spinner />
        )}
      </Col>
    </>
  );
};

export default AlgorithmDetailOverview;
